// Copyright (C) Unite Digital LLC - All Rights Reserved.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.
// Developed by Unite Digital LLC.

import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DealerProducts } from '@interfaces/dealer.interfaces';
import { AppEvent, NavItem } from '@interfaces/interfaces';
import { AccountType, User, UserDealers } from '@interfaces/user.interfaces';
import { ContentService } from '@services/content.service';
import { DealershipService } from '@services/dealership.service';
import { NavService } from '@services/nav.service';
import { PermissionsService } from '@services/permissions.service';
import { SettingsService } from '@services/settings.service';
import { UserService } from '@services/user.service';
import { Observable, takeWhile } from 'rxjs';
import { ErrorModalComponent } from 'src/app/modals/error-modal/error-modal.component';
import { tenantIsStellantis } from 'src/app/utils/utils';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'dealer-user-header',
    templateUrl: './dealer-user-header.component.html',
    styleUrls: ['./dealer-user-header.component.scss'],
})
export class DealerUserHeaderComponent implements OnInit, OnDestroy {
    public accountType: AccountType = null;
    public currentDealershipName: string = null;
    private dealershipLogo: any = null;
    private isComponentAlive = true;
    public menu$: Observable<NavItem[]>;
    public sideNav$: Observable<NavItem[]>;
    public selectedDealer: UserDealers = null;
    public showUserName = false;
    public user: User = null;
    public hasPrintToDdjEnrollment = false;
    public enrollmentAllowsFullAccess = false;

    constructor(
        private readonly dealershipService: DealershipService,
        private readonly dialog: MatDialog,
        private readonly navService: NavService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly userService: UserService,
        private readonly permissionService: PermissionsService,
        private readonly contentService: ContentService,
        readonly settingsService: SettingsService
    ) {
        this.showUserName = window.innerWidth < 768 ? false : true;
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.showUserName = window.innerWidth < 768 ? false : true;
        // This is sometimes triggering on page refresh before the user is loaded
        if (this.user) {
            this.menu$ = this.navService.getUserMenu(this.user);
        }
    }

    ngOnInit(): void {
        // subscribe to user changes from account settings/user management
        this.userService
            .getUser$()
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((user) => {
                this.user = user;
                // Resize handles getting the user menu
                this.onResize();
            });

        // set initial dealership logo
        this.dealershipService.dealership = this.route.snapshot.data.dealership;
        this.selectedDealer = this.route.snapshot.data.dealership;

        this.dealershipService
            .getDealerEnrollment$(this.selectedDealer.dealerId)
            .pipe(takeWhile(() => this.isComponentAlive))
            .subscribe((dealerEnrollment) => {
                for (const enrollment of dealerEnrollment) {
                    if (DealerProducts.EzDocsAutomate === enrollment.productId) {
                        this.hasPrintToDdjEnrollment = enrollment.isActive;
                    } else if (DealerProducts.DriverLicenseScan === enrollment.productId) {
                        this.enrollmentAllowsFullAccess = enrollment.isActive;
                    }
                }
            });

        this.navService.accountType = tenantIsStellantis(this.dealershipService.dealership.oemCode) ? AccountType.stellantisDealer : AccountType.ddjDealer;

        this.getDealershipUpdates();
    }

    ngOnDestroy(): void {
        this.isComponentAlive = false;
    }

    public eventHandler(event: AppEvent): void {
        // update the user menu when dealer changes
        if (event.context === 'dealer-changed') {
            this.menu$ = this.navService.getUserMenu(event.data);
            this.navService.setRouteState(this.route.snapshot);
        }
    }

    private getDealershipUpdates(): void {
        this.dealershipService.dealershipUpdate$.pipe(takeWhile(() => this.isComponentAlive)).subscribe(() => {
            this.currentDealershipName = this.dealershipService.dealershipName;
            this.setDealerLogo();
        });
    }

    private async logout(): Promise<void> {
        this.navService.accountType = null;
        this.userService.logOut();
        await this.router.navigate(['/login']);
    }

    public async navigate(item: NavItem): Promise<void> {
        if (item.route.includes('login')) {
            await this.logout();
        } else if (this.permissionService.hasPermission(item?.permissions?.permissionIds)) {
            this.navService.setSelectedRoute('dashboard', item);
        } else {
            const errorMessage = await this.contentService.getAccessRestrictedContent(
                this.selectedDealer.dealerId,
                this.selectedDealer.oemCode,
                item.permissions?.permissionIds
            );

            errorMessage.pipe(takeWhile(() => this.isComponentAlive)).subscribe((message) => {
                this.dialog.open(ErrorModalComponent, {
                    width: '700px',
                    data: {
                        title: 'Access Restricted',
                        message,
                    },
                });
            });
        }
    }

    public routeTo(): void {
        this.router.navigateByUrl('/dashboard/leads');
    }

    private setDealerLogo(): void {
        if (this.dealershipService.dealership.file !== undefined && this.dealershipService.dealership.file !== null) {
            if (this.dealershipService.dealership.file.Body) {
                this.dealershipService.dealership.file.Body = new Uint8Array(this.dealershipService.dealership.file.Body.data);
            }
            if (this.dealershipService.dealership.file.ContentDisposition) {
                this.dealershipService.dealership.file.fileName = this.dealershipService.dealership.file.ContentDisposition.split(';')[1]
                    .split('filename')[1]
                    .split('=')[1]
                    .trim();
            }
            this.dealershipLogo = window.URL.createObjectURL(
                new Blob([this.dealershipService.dealership.file.Body], {
                    type: 'application/jpg',
                })
            );

            const image = document.getElementById('logo') as HTMLImageElement;
            if (this.dealershipService.dealership.file.content) {
                image.src = 'data:image/webp;base64, ' + this.dealershipService.dealership.file.content; // this.dealershipLogo;
            } else {
                image.src = this.dealershipLogo;
            }
        } else {
            const image = document.getElementById('logo') as HTMLImageElement;
            image.classList.add('ez-docs-sizing');
            image.src = '../assets/img/5-ez-docs-color.png';
        }
    }
}
